<template>
<div v-if="!isReadonly" data-component="FilterDetailsQuick.vue" @change="isFilterDirty = true">
	<div class="pb-3">
		<div class="heading-small mt-3" v-html="'Szybki Kontakt'"/>

		<!-- Osoba kontaktowa -->
		<div class="form-input">
			<select data-test="quick_contact__contact_person" v-model="selectedFilter.idCustomerContactUser" class="appearance-none" @change="changeContactPerson($event)">
				<option
					v-for="person in servicesObject.contactPersons"
					:key="person.id"
					:value="person.id"
				>
					✉️ {{ person.email }}
					👤 {{ person.fullName }}
					☎️ {{ personPhone(person) }}
				</option>
			</select>
			<label class="form-input-label | text-xs uppercase my-1" v-html="'Osoba do kontaktu'"/>
		</div>

		<!-- Powiadomienia SMS -->
		<div class="form-input">
			<input data-test="quick_contact__number" v-model="selectedFilter.quickContactSmsNumber" type="text" required>
			<label class="form-input-label | text-xs uppercase my-1" v-html="'Powiadomienia SMS'"/>
		</div>

		<!-- Cena kontaktu -->
		<div class="form-input" :data-invalid="contactPriceError">
			<input
				data-test="quick_contact__price"
				v-model="selectedFilter.contactPrice"
				type="number"
				required
				:placeholder="String(selectedFilter.contactPrice || selectedFilter.minContactPrice)"
				@blur="fixContactPrice"
			>
			<label class="form-input-label | text-xs uppercase my-1" v-html="'Cena kontaktu'"/>
			<div
				v-if="contactPriceError"
				class="form-input-validation | text-danger text-xs mt-2"
				v-html="'Cena kontaktu powinna być ustawiona oraz być większa niż cena domyślna dla tej kategorii'"
			/>
		</div>
	</div>

	<FilterDetailsCustomize
		:selectedFilter="selectedFilter"
		@edit-matching="editMatching"
		@edit-locations="editLocations"
	/>
</div>
<div v-else data-component="FilterDetailsQuick.vue">
	<!-- Tylko do odczytu -->
	<div class="bg-neutral-100 p-3 mt-5 text-sm rounded">
		<div class="flex">
			<BaseIcon name="info" size="1.25" class="mr-3"/>
			<div>
				<div class="heading-tiny" v-html="'Tylko do odczytu'"/>
				W celu dokonania zmian skontaktuj się ze swoim opiekunem:
				<a class="flex items-center mt-1">
					<BaseIcon name="user-circle" size="1" class="mr-1"/>
					{{ servicesObject.operators[0].fullName }}
				</a>
				<a class="flex items-center mt-1" :href="`tel:${servicesObject.operators[0].phone}`">
					<BaseIcon name="phone" size="1" class="mr-1"/>
					{{ servicesObject.operators[0].phone }}
				</a>
				<a class="flex items-center mt-1" :href="`mailto:${servicesObject.operators[0].email}`">
					<BaseIcon name="envelope" size="1" class="mr-1"/>
					{{ servicesObject.operators[0].email }}
				</a>
			</div>
		</div>
		<button class="btn-primary btn-small block mx-auto mt-3" @click.prevent="contactOperator" v-html="'Proszę o kontakt'"/>
	</div>

	<!-- Informacje o filtrze -->
	<div class="divide-neutral-100 divide-y">
		<div class="py-3">
			<div class="font-extrabold" v-html="'Osoba do kontaktu'"/>
			<div class="text-neutral-700">
				<a class="flex items-center mt-1">
					<BaseIcon name="user-circle" size="1" class="mr-1"/>
					{{ selectedFilter.contactPerson.fullName }}
				</a>
				<a class="flex items-center mt-1">
					<BaseIcon name="phone" size="1" class="mr-1"/>
					{{ selectedFilter.contactPerson.phone || 'Nie podano' }}
				</a>
			</div>
		</div>
		<div v-if="selectedFilter.contactPrice" class="py-3">
			<div class="font-extrabold" v-html="'Cena kontaktu'"/>
			<div class="text-neutral-700" v-html="`${selectedFilter.contactPrice || 'Brak informacji o '} pkt.`"/>
		</div>
		<div class="py-3">
			<div class="font-extrabold" v-html="'Rodzaje zleceń'"/>
			<div class="text-neutral-700" v-html="filterTypesList.join(', ')"/>
		</div>
		<div class="py-3">
			<div class="font-extrabold" v-html="'Lokalizacja'"/>
			<div class="text-neutral-700" v-html="selectedFilter.location?.labels"/>
		</div>
	</div>
</div>
</template>

<script lang="ts">
import { mapState, mapStores, mapWritableState } from 'pinia';
import { useServicesStore } from '../../../../stores/services';
import { useAppStore } from '../../../../stores/app';
import Filter from '../../../../types/interfaces/services/Filter';
import { Services } from '../../../../types/interfaces/services/Services';
import FilterDetailsCustomize from './FilterDetailsCustomize.vue';
import ContactPerson from '../../../../types/interfaces/services/ContactPerson';
import serverFetch from '../../../../globals/ServerFetch';
import { useUserStore } from '../../../../stores/user';

export default {
	name: 'FilterDetailsQuick',
	components: {
		FilterDetailsCustomize,
	},
	props: {
		/**
		 * Główny obiekt usług
		 */
		servicesObject: {
			type: Object as () => Services,
			required: true,
		},

		/**
		 * Czy używamy widoku tylko do odczytu (dla użytkownika)?
		 */
		isReadonly: {
			type: Boolean,
			default: false,
		},

		/**
		 * Aktualnie wybrany filtr
		 */
		filter: {
			type: Object as () => Filter,
			required: true,
		},

		/**
		 * Początek ścieżki do API.
		 */
		apiNamespace: {
			type: String,
			required: true,
		},
	},
	emits: ['save-filter', 'edit-locations', 'edit-matching'],
	data() {
		return {
			/**
			 * Aktualnie wybrany filtr
			 */
			selectedFilter: this.filter,
		};
	},
	computed: {
		...mapStores(useUserStore),
		...mapState(useAppStore, [
			'isAdminModule',
		]),
		...mapWritableState(useServicesStore, [
			'isFilterDirty',
		]),

		/**
		 * Tablica z rodzajami zleceń do wyświetlenia w trybie tylko do odczytu.
		 */
		filterTypesList() {
			const result = [];
			const { filterTypes } = this.selectedFilter;
			if (filterTypes.companies) {
				result.push('Firmy');
			}
			if (filterTypes.individuals) {
				result.push('Klienci indywidualni');
			}
			if (filterTypes.standardRfps) {
				result.push('Kontakty standardowe');
			}
			if (filterTypes.discountedRfps) {
				result.push('Kontakty z obniżoną ceną kontaktu');
			}
			return result;
		},

		/**
		 * Wyświetla komunikat walidacyjny, jeżeli cena kontaktu jest zbyt niska.
		 */
		contactPriceError() {
			const { contactPrice, minContactPrice } = this.selectedFilter;
			return ((contactPrice || -1) < minContactPrice);
		},
	},
	mounted() {
		if (this.isAdminModule) {
			this.setDefaultContactUser();
		}
	},
	methods: {
		/**
		 * Jeżeli cena minimalna nie jest ustawiona lub jest niższa od ceny kontaktu, popraw ją.
		 */
		fixContactPrice() {
			if (this.contactPriceError) {
				this.selectedFilter.contactPrice = this.selectedFilter.minContactPrice;
			}
		},

		/**
		 * W przypadku wejścia na stronę przez operatora i gdy filtr nie ma przypisanej
		 * osoby kontaktowej, weź przypisz mu pierwszą lepszą.
		 */
		setDefaultContactUser() {
			if (!this.isAdminModule || this.selectedFilter.idCustomerContactUser > -1) {
				return;
			}

			this.selectedFilter.idCustomerContactUser = 0;
			this.changeContactPerson();
		},

		/**
		 * Przy zmianie osoby kontaktowej, przypisuje jej numer telefonu do filtra.
		 */
		changeContactPerson(event?: Event) {
			if (event) {
				const { value } = event.target as HTMLInputElement;
				this.selectedFilter.contactPerson.contactPersonId = Number(value);
				this.selectedFilter.idCustomerContactUser = Number(value);

				const person = this.servicesObject.contactPersons.find((contectPerson) => contectPerson.id === Number(value));

				if (person) {
					this.selectedFilter.quickContactSmsNumber = person.mobile || person.phone;
				}

				this.isFilterDirty = true;

				return;
			}

			const person = this.servicesObject.contactPersons.find((contectPerson) => contectPerson.id === this.selectedFilter.idCustomerContactUser);

			if (!person) {
				return;
			}

			this.selectedFilter.quickContactSmsNumber = person.mobile || person.phone;
			this.isFilterDirty = true;
		},

		/**
		 * Wysyła wiadomość do operatora klientu z prośbą o dopasowanie filtra.
		 */
		async contactOperator() {
			await serverFetch(
				`/${this.apiNamespace}/constraintsContact.ofer`,
				{
					subject: `Klient ${this.userStore.customerId} - Dopasowanie Klientów, prośba o kontakt`,
					idSubject: 'contactConfiguration',
					surname: this.userStore.lastName,
					email: this.userStore.email,
					telephon: this.userStore.phone,
				},
				'POST',
			);

			this.Toast.Show({ Message: 'Prośba o kontakt została wysłana' });
		},

		/**
		 * Przenosi użytkownika do edycji Lokalizacji
		 * @emits editLocations
		 */
		editLocations({ filter, display }: { filter: Filter, display: boolean }) {
			this.$emit('edit-locations', { filter, display });
		},

		/**
		 * Przenosi użytkownika do edycji Filtrów
		 * @emits editMatching
		 */
		editMatching() {
			this.$emit('edit-matching');
		},
		personPhone(person: ContactPerson) {
			if ((!person.mobile || person.mobile === 'null') && (!person.phone || person.phone === 'null')) {
				return 'Brak numeru';
			}

			return person.mobile || person.phone;
		},
	},
};
</script>
